<script>
import TabNavigation from "../components/TabNavigation.vue";
import CurrencyInput from "../components/currencyInput/currencyInput.vue";
import DropdownSelect from "../components/dropdownSelect.vue";
import {useDonationSessionStore} from "../../stores/donationSession";
import {useAnalyticEventStore} from "../../stores/analyticEvents";

export default {
  name: "DonationInput",
  components: {DropdownSelect, CurrencyInput, TabNavigation},
  props: {
    donationAmount: {
      type: [Number, String],
      default: 0
    },
    suggestedAmounts: {
      type: Array,
      required: false,
    },
    subscriptionIntervals: {
      type: Array,
      required: true
    },
    subscriptionsEnabled: {
      type: Boolean,
      default: true
    }
  },
  emits: ['add-to-cart', 'checkout', 'update:donationAmount'],
  setup() {
    const donationSessionStore = useDonationSessionStore();
    const analyticEventStore = useAnalyticEventStore();
    return {
      donationSessionStore,
      analyticEventStore
    };
  },
  data() {
    return {
      donationError: false,
      addingToBasket: false,
      activeTab: 'one-time',
      selectedInterval: "",
      intervalMenuOpen: false,
      sliderWidth: 50, // Initial width of the slider (50%)
      isAnimating: false,
    };
  },
  computed: {
    donateButtonEnabled() {
      const amountValid = +this.donationAmount >= 5;
      const intervalValid = this.activeTab === 'one-time' || this.selectedInterval;
      return amountValid && intervalValid;
    },
    recurringGiftText() {
      if (!this.selectedInterval) {
        return "Recurring Gift";
      }
      return `${this.selectedInterval} Gift`;
    },

  },
  mounted() {
    this.selectDefaultAmount();
  },
  methods: {
    selectDefaultAmount() {
      if(this.donationAmount < 5 && this.suggestedAmounts?.length > 0 ) {
        // Get the mid most option TODO - allow setting of the default option in campaign settings?
        const middleOption = Math.floor((this.suggestedAmounts.length - 1) / 2);
        this.$emit('update:donationAmount', this.suggestedAmounts[middleOption]);
      }
    },

    recordAnalyticEvent(action, amount) {
      this.analyticEventStore.setOrganizationId(this.donationSessionStore.organizationId);
      this.analyticEventStore.logEvent({
        action: action, metadata: amount },
      );
    },

    toggleSwitch(tab) {
      if (tab === this.activeTab) {
        return false;
      }
      this.intervalMenuOpen = false; // Close the interval menu

      if (this.isAnimating) return; // Prevent multiple clicks during animation
      this.isAnimating = true;

      // First, expand the slider width to 100%
      this.animateSlider(100, () => {
        // After expanding, toggle the state
        this.activeTab = tab;

        // Now, shrink the slider width back to 50%
        this.animateSlider(50, () => {
          this.isAnimating = false;
          if (tab === 'recurring' && !this.selectedInterval) {
            this.intervalMenuOpen = true; // Open the Interval Menu by default if option not selected
          }
        });
      });
    },

    // Slide animation logic
    animateSlider(targetWidth, callback) {
      const initialWidth = this.sliderWidth;
      const duration = 150; // Animation duration in ms
      const step = 16;

      const totalFrames = Math.floor(duration / step);
      let currentFrame = 0;

      const animate = () => {
        currentFrame++;

        const progress = currentFrame / totalFrames;
        // Interpolate width between initialWidth and targetWidth
        this.sliderWidth = initialWidth + (targetWidth - initialWidth) * progress;

        if (currentFrame < totalFrames) {
          requestAnimationFrame(animate);
        } else {
          this.sliderWidth = targetWidth;
          if (callback) callback();
        }
      };

      requestAnimationFrame(animate);
    },

    addDonationToCart() {
      if (!this.donateButtonEnabled) return;
      this.addingToBasket = true;
      setTimeout(() => { // Built in delay to prevent double clicking basket button
        this.$emit('add-to-cart', {type: this.activeTab, interval: this.selectedInterval});
        this.addingToBasket = false;
      }, 500);
    },

    immediateCheckout() {
      if (!this.donateButtonEnabled) return;
      this.$emit('checkout', {type: this.activeTab, interval: this.selectedInterval});
    },

    suggestedAmountOnClick(amount) {
      this.$emit('update:donationAmount', amount);
    },

    openMenu() {
      if (this.activeTab === 'recurring') {
        this.intervalMenuOpen = !this.intervalMenuOpen;
      }
    },

    selectInterval(interval) {
      this.selectedInterval = interval;
    },

    formattedSuggestedAmount(amount) {
      if (Number.isInteger(amount)) {
        return amount.toString(); // Return as a string without decimals if it's a whole number
      } else {
        return amount.toFixed(2).toString(); // Round to 2 decimal places and return as a string
      }
    },

    // Dropdown meny animation functions, ripped from Dropdown select
    beforeEnter: function (el) {
      el.style.height = '0';
    },
    enter: function (el) {
      el.style.height = el.scrollHeight + 'px';
    },
    beforeLeave: function (el) {
      el.style.height = '0';
    },
    leave: function (el) {
      el.style.height = '0';
    },
  },
};
</script>


<template>
  <div class="donation-input-container donation-section mt-8 ">
    <!-- Custom Toggle Switch with text inside -->
    <div class="toggle-container">
      <div
        class="switch"
        :class="{ 'toggled': activeTab === 'recurring' }"
      >
        <div
          class="tab-label cursor-pointer"
          @click="toggleSwitch('one-time'); recordAnalyticEvent('checkout-one-time-gift-selected')"
        >
          One Time Gift
        </div>
        <div
          class="tab-label cursor-pointer"
          @click="toggleSwitch('recurring'); recordAnalyticEvent('checkout-recurring-gift-selected')"
        >
          {{ recurringGiftText }}
        </div>

        <div
          ref="slider"
          class="slider"
          :class="{ 'menu-open': intervalMenuOpen }"
          :style="{ width: sliderWidth + '%' }"
          @click="openMenu"
        >
          <div
            v-if="activeTab === 'recurring'"
          >
            <div
              :class="{ 'open': intervalMenuOpen }"
              class="recurring-options-menu"
            >
              <transition
                name="dropdown-transition"
                @before-enter="beforeEnter"
                @enter="enter"
                @before-leave="beforeLeave"
                @leave="leave"
              >
                <ul
                  v-show="intervalMenuOpen"
                  class="dropdown-menu"
                >
                  <li
                    v-for="interval in subscriptionIntervals"
                    :key="interval?.toString()"
                    class="interval-option"
                    @click="selectInterval(interval)"
                  >
                    {{ interval }}
                  </li>
                </ul>
              </transition>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="donation-selection-container">
      <div v-if="suggestedAmounts.length > 0">
        <div class="caption-bold mt-2">
          Suggested Gifts
        </div>
        <div
          id="suggested-amounts-container"
        >
          <div
            v-for="amount in suggestedAmounts"
            :key="amount"
          >
            <div
              class="suggested-amount"
              :class="[{'selected' : Number(amount) === Number(donationAmount)}]"
              @click="suggestedAmountOnClick(amount); recordAnalyticEvent('checkout-click-suggested-amount', { suggestedAmount: amount })"
            >
              ${{ formattedSuggestedAmount(amount) }}
            </div>
          </div>
        </div>
      </div>


      <div class="w-full donation-amount-container mt-2">
        <div class="caption-bold mt-2 mb-1">
          Your Gift
        </div>

        <currency-input
          id="donationAmount"
          :model-value="donationAmount"
          prefix="$ "
          :max="10000"
          @update:model-value="value => $emit('update:donationAmount', value)"
        />
        <span
          v-if="donationError"
          class="text-xs text-red-700"
        >
          Please enter a gift more than $5
        </span>
      </div>
      <div
        class="donation-buttons-container flex flex-row"
      >
        <button
          class="add-to-basket-button button"
          :class="[{
            'full': activeTab === 'one-time',
            'gone': activeTab === 'recurring',
            'disabled': !donateButtonEnabled,
            'loading': addingToBasket }]"
          @click="addDonationToCart"
        >
          Add To Basket
        </button>
        <button
          class="donate-now-button button"
          :class="[{
            'full': activeTab === 'recurring',
            'gone': activeTab === 'one-time',
            'disabled': !donateButtonEnabled,
            'loading': addingToBasket }]"
          @click="immediateCheckout"
        >
          Give Now
        </button>
      </div>
    </div>
  </div>
</template>


<style lang="scss" scoped>
@import '@/styles/_variables.scss';


.donate-now-button, .add-to-basket-button {
  transition: width 500ms cubic-bezier(.59, 1.39, .37, 1.01), background-color .2s ease-in-out;
  height: 50px;
  max-height: 50px;

  &.half {
    width: 50%;
  }

  &.full {
    width: 100%;
  }

  &.gone {
    width: 0;
    height: 0;
    padding: 0;
    margin: 0;
    overflow: hidden;
  }


}

/* Custom toggle switch styles */
.toggle-container {
  display: flex;
  width: 100%;
  position: relative;
  z-index: 1;
}

.switch {

  position: relative;
  width: 100%;
  height: 40px;
  border-radius: 30px;
  cursor: pointer;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  transition: 0.4s;
}

.switch.toggled {
  justify-content: flex-end;
}

.slider {

  /**
  background-color: var(--color-primary);
    border-radius: 50px;
   **/

  border-bottom: 4px solid var(--color-primary);

  position: absolute;

  transition: transform 0.4s;
  width: 50%;
  height: 100%;
  display: flex;

  justify-content: center;
  align-items: center;


  .recurring-options-menu {
    overflow: hidden;
    position: absolute;
    background-color: white;
    opacity: 1;
    z-index: 100;
    width: 100%;
    top: 100%;
    left: 0;
    display: flex;
    flex-direction: column;
    padding-bottom: 10px;
    border-radius: 0 0 25px 25px;

    @apply shadow-lg;
    .dropdown-menu {
      max-height: 500px;
      transition: 150ms linear;
      z-index: 1000;
    }

    .interval-option {
      @apply rounded-sm p-2 mx-2;
      display: flex;
      justify-content: center;
      align-items: center;
      text-transform: capitalize;

      &:hover {
        background-color: var(--color-primary);
        color: var(--color-primary-text);
      }
    }

    &:not(.open) {
      height: 0;
      visibility: hidden;

      .interval-option {
        visibility: hidden;
      }
    }
  }

  &.menu-open {
    border-radius: 25px 25px 0 0;
  }

}

.tab-label, .interval-option {
  font-weight: 400;
  transition: opacity 0.4s;
  text-transform: capitalize;
}

.tab-label {
  width: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: transform 0.4s;
}


.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

.donation-section {
  background-color: white;
  border-radius: $radius-rounded;
  box-shadow: $shadow-card;

  input, :deep(input), select {
    @apply bg-white;
    @apply w-full px-2 py-2 rounded-lg shadow-sm focus:outline-none cursor-pointer;
    @apply text-lg leading-normal font-semibold;
    border: 2px solid var(--color-primary);
  }

}

button, .button {
  @apply w-full rounded font-semibold text-base text-center content-center;

  background-color: var(--color-primary);
  color: var(--color-primary-text);

  &:hover {
    background-color: var(--color-primary--dark);
  }

  :deep(.circle-progress) {
    .circle-progress-fill-line {
      stroke: var(--color-primary-text);
    }
  }

  &.loading {
    @apply opacity-30 cursor-progress;
  }

  &.disabled {
    @apply opacity-30 cursor-not-allowed;
  }
}

.donation-input-container {
  #suggested-amounts-container {
    display: grid;
    @apply mt-1 mb-2 gap-3;


    .suggested-amount {
      @apply font-semibold text-base bg-white rounded-md cursor-pointer;
      @apply flex items-center justify-center;

      border: 2px solid var(--color-primary);
      transition: background-color 0.3s, color 0.3s, border 0.3s;

      &:hover, &.selected {
        background-color: var(--color-primary);
        border-color: var(--color-primary--dark);
        color: var(--color-primary-text);
      }
    }
  }
}



@screen xs {
  .donation-input-container {
    margin: 0;
    width: 100%;

    padding: $space-lg;

    #suggested-amounts-container {
      grid-template-columns: repeat(4, 1fr);

      .suggested-amount {
        aspect-ratio: unset;
        min-height: 60px;;

        &:hover, &.selected {
          background-color: var(--color-primary);
          border-color: var(--color-primary--dark);
          color: var(--color-primary-text);
        }
      }
    }
    .donation-buttons-container {
      @apply mt-4;
    }
  }
}

@screen sm {
  .donation-section {
    #suggested-amounts-container {
      grid-template-columns: repeat(4, 1fr);
    }
  }
}

@screen md {
  .donation-input-container {
    padding: $space-lg;

    #suggested-amounts-container {
      grid-template-columns: repeat(4, 1fr);
    }
    .donation-amount-container {
      @apply mt-4;
    }
    .donation-buttons-container {
      @apply mt-4;
    }
  }
}

@screen lg {
  .donation-input-container {
    #suggested-amounts-container {
      .suggested-amount {
        aspect-ratio: 1 / 1;
      }
    }
  }
}

</style>
